import { ControlTemplate } from 'dac-web-ui';
import css from './list.module.css';
import html from './list.module.hbs';
import { GROUP_TYPE } from '../../js/structures/aqua/gridAqua';
import { CellDivision } from '../cellDivision/cellDivision';

//loaders from https://css-loaders.com/
export class List extends ControlTemplate {

	constructor( containerId, data ) {
		super();
		this.containerId = containerId;
		this.selectedData = data;
	}

	show( data ) {
		this.data = this.#sortData( data );

		//add css
		let idcss = 'listCss';

		//remove
		let old = document.head.querySelector( '#' + idcss );
		if( old ) old.parentNode.removeChild( old );

		//add
		let styleSheet = document.createElement( 'style' );
		styleSheet.id = idcss;
		styleSheet.type = 'text/css';
		styleSheet.innerText = css;

		document.head.appendChild( styleSheet );

		//add html
		this.container = document.getElementById( this.containerId );
		if( this.container ) {
			this._addHtml( this.container, this.#getItemsFromData(), html );
			this.#addEventHandlers();
			this.#scrollToSelected();
		}
	}

	#sortData( data ) {
		// sort by direction, if any, and then by inicio
		return data.sort( ( a, b ) => {
			if( a.direction && b.direction ) {
				if( a.direction === b.direction ) return a.inicio - b.inicio;
				return a.direction - b.direction;
			}
			return a.inicio - b.inicio;
		});
	}

	#getItemsFromData() {
		const items = [];
		for( let i = 0; i < this.data.length; i++ ) {
			const d = this.data[i];
			const item = {
				idHeader: 'ID',
				idDesc: 'Description',
				idInit: 'Init',
				idEnd: 'End',
				id: i, 
				desc: `name ${i}`, 
				inicio: d.inicio, 
				fin: d.fin,
				value: d.value,
				selected : this.selectedData === d ? 'listrowselected' : '',
				type: List.getOperationType( d )
			};

			switch( item.type ) {
				case GROUP_TYPE.TRAVERSALES: { item.desc = `transversal ${i}`; } break;
				case GROUP_TYPE.CAPILLAS: { item.desc = `capilla ${i}`; } break;
				case GROUP_TYPE.FRONTALES: { item.desc = `malla ${d.malla} ${d.direction}`; } break;
				case GROUP_TYPE.LATERALES: { item.desc = `capilla ${d.capilla} ${d.direction}`; } break;
			}

			// if it is frontal or lateral, save the line index
			item.index = d.type ===  GROUP_TYPE.FRONTALES ? d.malla : d.type === GROUP_TYPE.LATERALES ? d.capilla : -1;

			items.push( item );
		}
		
		return items;
	}

	#addEventHandlers() {
		//on divide click
		const divides = this.container.querySelectorAll('.listDivide');
		if( divides ) divides.forEach( divide => divide.onclick = async e => await this.#showDivisionForm( e ) );
		
		//on cancel click
		const dels = this.container.querySelectorAll('.listDelete');
		if( dels ) dels.forEach( del => del.onclick = e => this.#delete( e ) );

		//on input changed
		const inputs = this.container.querySelectorAll( 'input' );
		if( inputs ) inputs.forEach( input => input.oninput = e => this.#inputChanged( e ) );

		// on row selected
		const rows = this.container.querySelectorAll( '.listrow' );
		if( rows ) rows.forEach( row => row.onclick = e => this.#rowSelected( e ) );
	}

	#rowSelected( e ) {
		//get row div
		let row = e.target;
		while( row.classList.contains( 'listrow' ) === false ) row = row.parentNode;
		
		const i = parseInt( row.dataset.index );
		const d = this.data[Math.min( i, this.data.length - 1 )];
		if( ! d ) return;
		this.selectedData = d;

		//remove selected from all rows
		const rows = this.container.querySelectorAll( '.listrow' );
		rows.forEach( r => r.classList.remove( 'listrowselected' ) );
		//add selected to the clicked row
		row.classList.add( 'listrowselected' );

		//trigger event
		this.trigger( 'rowSelected', d, List.getOperationType( d ) );
		
		this.#scrollToSelected();
	}

	#scrollToSelected() {
		if( !this.selectedData ) return;
		const row = this.container.querySelector( '.listrowselected' );
		if( !row ) return;
		row.scrollIntoView();
	}

	#inputChanged( e ) {
		const indexStr = e.target.dataset.index;
		const type = e.target.dataset.type;
		if( typeof indexStr !== 'undefined' && indexStr !== null ) {
			const value = e.target.value;
			try {
				const index = parseInt( indexStr );
				const number = parseFloat( value );
				// check whether is possible to set this values
				switch( type ) {
					case 'init': this.data[index].inicio = number; break;
					case 'end': this.data[index].fin = number; break;
				}

				// ReDraw 2D
				this.trigger( 'dataChanged', this.data, index );
			} catch( e ) {
				console.log( e );
			}
		}
	}

	async #showDivisionForm( e ) {
		const indexStr = e.target.dataset.index;
		const index = parseInt( indexStr );
		const dat = this.data[index];
		
		if( dat.inicio === dat.fin ) return;

		const division = await new CellDivision( 'modalContainer' ).show( dat.inicio, dat.fin );

		if ( !division.ok ) return;

		this.#divide( dat, division.newInit, division.newEnd );
	}

	#divide( dat, newInit, newEnd ) {
		try {
			if( dat.inicio === dat.fin ) return;

			//cut in half
			const parts = List.cutInHalf( dat, newInit, newEnd );
			//remove the original and add the parts
			const index = this.data.indexOf( dat );
			this.data.splice( index, 1, ...parts );

			this.selectedData = parts[0];

			this.hide();
			this.show( this.data );

			// ReDraw 2D
			this.trigger( 'dataChanged', this.data, index );
		} catch( e ) {
			console.log( e );
		}
	}

	static cutInHalf( data, newInit, newEnd ) {
		const inicio = data.inicio;
		const fin = data.fin;
		const backwards = inicio > fin;

		//copy data to create two new parts
		const part1 = { ...data };
		const part2 = { ...data };
		if( newInit === data.inicio ) {
			part1.inicio = newInit;
			part1.fin = newEnd;
			part2.inicio = backwards ? newEnd - 1 : newEnd + 1;
			part2.fin = data.fin;
		} else {
			part1.inicio = data.inicio;
			part1.fin = backwards ? newInit + 1 : newInit - 1;
			part2.inicio = newInit;
			part2.fin = newEnd;
		}

		// split lists also
		const type = List.getOperationType( data );
		const line = type === GROUP_TYPE.FRONTALES ? 'MIDColumna' :  
			type === GROUP_TYPE.LATERALES ? 'MIDFila' :  
				type === GROUP_TYPE.CAPILLAS ? 'columna' : 
					type === GROUP_TYPE.TRAVERSALES ? 'fila' : '';
		if( line === '' ) return [ part1, part2 ];

		const listName = line === 'MIDFila' || line === 'MIDColumna' ? 'cells' : 
			line === 'fila' ? 'transversales' :	
				line === 'columna' ? 'capillas' : '';

		part1[listName] = [];
		part2[listName] = [];

		const isInside = ( v, i1, i2 ) => Math.min( i1,i2) <= v && v <= Math.max( i1, i2 );

		for( let i = 0; i < data[listName].length; i++ ) {
			const cell = data[listName][i];
			if( isInside( cell[line], part1.inicio, part1.fin ) ) part1[listName].push( cell );
			else part2[listName].push( cell );
		}

		return [ part1, part2 ];
	}

	#delete( e ) {
		const indexStr = e.target.dataset.index;
		const type = e.target.dataset.type;

		if( typeof indexStr !== 'undefined' && indexStr !== null ) {
			try {
				const index = parseInt( indexStr );

				const dat = this.data[index];
				const backwards = dat.inicio > dat.fin;

				// Count how many rows there are of the same direction. If there is only one of the same direction, return
				let dats = [];

				//take into account only cells within the same horizontal or vertical line (malla or capilla)
				if( type === GROUP_TYPE.FRONTALES || type === GROUP_TYPE.LATERALES ) {
					const line = type === GROUP_TYPE.FRONTALES ? 'malla' :  type === GROUP_TYPE.LATERALES ? 'capilla' : '';
					this.data.forEach( d => { if( d.direction === dat.direction && d[line] === dat[line] ) dats.push( d ); } );
				}
				else
					this.data.forEach( d => dats.push( d ) );

				if( dats.length === 1 ) return;

				//merge with next if possible, otherwise with previous if possible
				let toMerge = dats.find( d => backwards ? d.fin === dat.inicio + 1 : d.fin === dat.inicio - 1 );
				if( !toMerge ) toMerge = dats.find( d => backwards ? d.inicio === dat.fin - 1 : d.inicio === dat.fin + 1 );
				toMerge.inicio = backwards ? Math.max( toMerge.inicio, dat.inicio ) : Math.min( toMerge.inicio, dat.inicio );
				toMerge.fin = backwards ? Math.min( toMerge.fin, dat.fin ) : Math.max( toMerge.fin, dat.fin );

				const listName = type === GROUP_TYPE.FRONTALES || type === GROUP_TYPE.LATERALES ? 'cells' : 
					type === GROUP_TYPE.TRAVERSALES ? 'transversales' :	
						type === GROUP_TYPE.CAPILLAS ? 'capillas' : '';

				toMerge[listName] = toMerge[listName].concat( dat[listName] );

				//remove the original
				this.data.splice( index, 1 );

				this.selectedData = toMerge;

				this.hide();
				this.show( this.data );

				//ReDraw 2D
				this.trigger( 'dataChanged', this.data, index );
			} catch( e ) {
				console.log( e );
			}
		}
	}

	hide() {

		//remove css
		let idcss = 'listCss';
		let old = document.head.querySelector( '#' + idcss );
		if( old ) old.parentNode.removeChild( old );

		//remove html
		if( this.container ) this.container.innerHTML = '';
	}

	static getOperationType( data ) {
		if( typeof data.malla !== 'undefined' ) return GROUP_TYPE.FRONTALES;
		if( typeof data.capilla !== 'undefined' ) return GROUP_TYPE.LATERALES;
		if( typeof data.transversales !== 'undefined' ) return GROUP_TYPE.TRAVERSALES;
		if( typeof data.capillas !== 'undefined' ) return GROUP_TYPE.CAPILLAS;

		return -1;
	}
}